import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  Trophy,
  DollarSign,
  Calendar,
  TrendingUp,
  Wallet,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useSuccessPackage } from '../hooks';
import { SuccessPackageAction } from '../components';
import { useAuthStore } from '@/features/auth/stores/auth.store';

function InfoField({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
          {label}
        </p>
        <p className="text-sm font-medium text-slate-900">
          {value || '-'}
        </p>
      </div>
    </div>
  );
}

function formatPaymentDisplay(value: number, unit: string): string {
  if (unit === 'PERCENT') return `${value}%`;
  return value?.toLocaleString() ?? '0';
}

export function SuccessPackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { data: pkg, isLoading, isError, error } = useSuccessPackage(id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-6 w-24" />
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !pkg) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <Trophy className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">
                {(error as Error)?.message || t('failedToLoadSuccessPackage')}
              </p>
            </div>
            <Link to="/app/success-packages">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToSuccessPackages')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Link
            to="/app/success-packages"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToSuccessPackages')}
          </Link>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg bg-emerald-100">
                <Trophy className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {pkg.name}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                  #{String(pkg.id).padStart(5, '0')}
                </span>
              </div>
            </div>
            {hasAccess('success-package', 'update') && (
              <SuccessPackageAction id={pkg.id} />
            )}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('minClaimAmount')}
              </p>
              <p className="text-xl font-bold text-slate-900">{pkg.minClaimAmount?.toLocaleString()}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('maxClaimAmount')}
              </p>
              <p className="text-xl font-bold text-slate-900">{pkg.maxClaimAmount?.toLocaleString()}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('advancePayment')}
              </p>
              <p className="text-xl font-bold text-blue-600">
                {formatPaymentDisplay(pkg.advancePayment, pkg.advancePaymentUnit)}
              </p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('successPayment')}
              </p>
              <p className="text-xl font-bold text-emerald-600">
                {formatPaymentDisplay(pkg.successPayment, pkg.successPaymentUnit)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Claim Amounts */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-slate-400" />
                {t('claimAmounts')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField icon={TrendingUp} label={t('minClaimAmount')} value={pkg.minClaimAmount?.toLocaleString()} />
              <InfoField icon={TrendingUp} label={t('maxClaimAmount')} value={pkg.maxClaimAmount?.toLocaleString()} />
            </CardContent>
          </Card>

          {/* Payment Structure */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-slate-400" />
                {t('paymentStructure')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField
                icon={DollarSign}
                label={t('advancePayment')}
                value={
                  <span>
                    {formatPaymentDisplay(pkg.advancePayment, pkg.advancePaymentUnit)}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {t(pkg.advancePaymentUnit.toLowerCase())}
                    </Badge>
                  </span>
                }
              />
              <InfoField
                icon={Trophy}
                label={t('successPayment')}
                value={
                  <span>
                    {formatPaymentDisplay(pkg.successPayment, pkg.successPaymentUnit)}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {t(pkg.successPaymentUnit.toLowerCase())}
                    </Badge>
                  </span>
                }
              />
            </CardContent>
          </Card>

          {/* System Info */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-400" />
                {t('systemInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('id')} value={String(pkg.id)} />
              <InfoField
                label={t('createdAt')}
                value={pkg.createdAt ? format(new Date(pkg.createdAt), 'MMM d, yyyy HH:mm') : '-'}
              />
              <InfoField
                label={t('updatedAt')}
                value={
                  pkg.updatedAt ? (
                    <span>
                      {format(new Date(pkg.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatDistanceToNow(new Date(pkg.updatedAt), { addSuffix: true })})
                      </span>
                    </span>
                  ) : '-'
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
